import { useState, useMemo, useCallback, useEffect } from 'react';
import { differenceInDays, parseISO } from 'date-fns';

// Mock user role for permission system
const CURRENT_USER_ROLE = 'manager'; // 'admin', 'manager', 'employee'

// Enterprise-grade status configuration
const DEFAULT_STATUS_CONFIG = [
  { 
    id: 'To Do', 
    label: 'To Do', 
    color: '#6B7280',
    theme: 'bg-gray-100',
    allowedTransitions: ['In Progress', 'Blocked'],
    isTerminal: false,
    wipLimit: null,
    description: 'Tasks ready to be started'
  },
  { 
    id: 'In Progress', 
    label: 'In Progress', 
    color: '#3B82F6',
    theme: 'bg-blue-100',
    allowedTransitions: ['In Review', 'Blocked', 'To Do'],
    isTerminal: false,
    wipLimit: 5,
    description: 'Tasks currently being worked on'
  },
  { 
    id: 'In Review', 
    label: 'In Review', 
    color: '#8B5CF6',
    theme: 'bg-purple-100',
    allowedTransitions: ['Done', 'In Progress', 'Blocked'],
    isTerminal: false,
    wipLimit: 3,
    description: 'Tasks awaiting review and approval'
  },
  { 
    id: 'Blocked', 
    label: 'Blocked', 
    color: '#EF4444',
    theme: 'bg-red-100',
    allowedTransitions: ['To Do', 'In Progress'],
    isTerminal: false,
    wipLimit: null,
    description: 'Tasks blocked by dependencies or issues'
  },
  { 
    id: 'Done', 
    label: 'Done', 
    color: '#10B981',
    theme: 'bg-green-100',
    allowedTransitions: ['In Review'],
    isTerminal: true,
    wipLimit: null,
    description: 'Completed tasks'
  },
];

const useTaskBoardController = () => {
  // Core State
  const [tasks, setTasks] = useState([]);
  const [currentView, setCurrentView] = useState('list');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedProject, setSelectedProject] = useState('all');
  const [sortBy, setSortBy] = useState('priority');
  const [sortDirection, setSortDirection] = useState('asc');
  const [selectedTasks, setSelectedTasks] = useState([]);
  const [isFilterPanelOpen, setIsFilterPanelOpen] = useState(true);
  const [density, setDensity] = useState('comfortable'); // 'compact', 'comfortable'
  const [undoStack, setUndoStack] = useState([]);
  const [statusConfig, setStatusConfig] = useState(DEFAULT_STATUS_CONFIG);

  // Filter State (TaskFilterDTO)
  const [filters, setFilters] = useState({
    search: '',
    projectId: 'all',
    status: [],
    ownerIds: [], // Replaced assigneeIds with ownerIds
    contributorIds: [], // New: filter by contributors
    checklistAssigneeIds: [], // New: filter by checklist assignees
    myWork: false, // New: quick filter for current user's work
    liveTasks: false, // New: quick filter for live tasks
    priorities: [],
    tags: [],
    dateRange: { start: '', end: '' },
    taskType: '',hasDependencies: ''
  });

  // Saved Views State
  const [savedViews, setSavedViews] = useState([
    {
      id: 'view1',
      name: 'My High Priority Tasks',
      isDefault: true,
      isShared: false,
      filters: { priorities: ['High', 'Critical'], assigneeIds: ['u1'] },
      sortBy: 'dueDate',
      viewType: 'list',
      visibleColumns: ['title', 'status', 'priority', 'assignee', 'dueDate', 'tags']
    },
    {
      id: 'view2',
      name: 'Team Sprint Tasks',
      isDefault: false,
      isShared: true,
      filters: { status: ['In Progress', 'In Review'] },
      sortBy: 'priority',
      viewType: 'kanban',
      visibleColumns: []
    },
  ]);

  // Initialize tasks with mock data
  useEffect(() => {
    // Mock users for reference
    const users = [
      { id: 'u1', name: 'Sarah Johnson', avatar: 'SJ', email: 'sarah.j@example.com', role: 'Product Designer' },
      { id: 'u2', name: 'Michael Chen', avatar: 'MC', email: 'michael.c@example.com', role: 'Senior Developer' },
      { id: 'u3', name: 'Emily Rodriguez', avatar: 'ER', email: 'emily.r@example.com', role: 'HR Manager' },
      { id: 'u4', name: 'David Kim', avatar: 'DK', email: 'david.k@example.com', role: 'DevOps Engineer' },
      { id: 'u5', name: 'Lisa Anderson', avatar: 'LA', email: 'lisa.a@example.com', role: 'UX Researcher' },
      { id: 'u6', name: 'Robert Taylor', avatar: 'RT', email: 'robert.t@example.com', role: 'Marketing Designer' },
      { id: 'u7', name: 'Ahmed Al-Mansoori', avatar: 'AA', email: 'ahmed.m@example.com', role: 'Facilities Manager' },
    ];

    setTasks([
      {
        id: 'TASK-001',
        title: 'Design new employee onboarding flow',
        description: 'Create comprehensive onboarding workflow with automated steps',
        status: 'In Progress',
        priority: 'High',
        assignees: [{ id: 'u1', name: 'Sarah Johnson', avatar: 'SJ', email: 'sarah.j@example.com' }],
        owner: { id: 'u1', name: 'Sarah Johnson', avatar: 'SJ', email: 'sarah.j@example.com', role: 'Product Designer' },
        contributors: [
          { id: 'u3', name: 'Emily Rodriguez', avatar: 'ER', email: 'emily.r@example.com', role: 'HR Manager' },
          { id: 'u5', name: 'Lisa Anderson', avatar: 'LA', email: 'lisa.a@example.com', role: 'UX Researcher' }
        ],
        checklist: [
          { id: 'cl-1', title: 'Create wireframes', completed: true, assignee: { id: 'u1', name: 'Sarah Johnson', avatar: 'SJ' }, dueDate: '2026-01-22', completedBy: 'u1', completedAt: '2026-01-22T14:30:00' },
          { id: 'cl-2', title: 'Review with HR team', completed: true, assignee: { id: 'u3', name: 'Emily Rodriguez', avatar: 'ER' }, dueDate: '2026-01-25', completedBy: 'u3', completedAt: '2026-01-25T10:15:00' },
          { id: 'cl-3', title: 'Conduct user testing', completed: false, assignee: { id: 'u5', name: 'Lisa Anderson', avatar: 'LA' }, dueDate: '2026-02-05' },
          { id: 'cl-4', title: 'Finalize design system', completed: false, assignee: { id: 'u1', name: 'Sarah Johnson', avatar: 'SJ' }, dueDate: '2026-02-10' }
        ],
        dueDate: '2026-02-15',
        startDate: '2026-01-20',
        tags: ['UX', 'HR'],
        taskType: 'Design',
        dependencies: ['TASK-002'],
        progress: 65,
        projectId: 'proj1',
        lastUpdated: '2026-01-23',
        createdDate: '2026-01-15',
        createdBy: 'u1',
        ownerId: 'u1',
        isLive: true
      },
      {
        id: 'TASK-002',
        title: 'Implement authentication module',
        description: 'Build secure authentication with OAuth and 2FA support',
        status: 'To Do',
        priority: 'High',
        assignees: [{ id: 'u2', name: 'Michael Chen', avatar: 'MC', email: 'michael.c@example.com' }],
        owner: { id: 'u2', name: 'Michael Chen', avatar: 'MC', email: 'michael.c@example.com', role: 'Senior Developer' },
        contributors: [
          { id: 'u4', name: 'David Kim', avatar: 'DK', email: 'david.k@example.com', role: 'DevOps Engineer' }
        ],
        checklist: [
          { id: 'cl-5', title: 'Setup OAuth providers', completed: false, assignee: { id: 'u2', name: 'Michael Chen', avatar: 'MC' }, dueDate: '2026-02-01' },
          { id: 'cl-6', title: 'Implement 2FA logic', completed: false, assignee: { id: 'u2', name: 'Michael Chen', avatar: 'MC' }, dueDate: '2026-02-05' },
          { id: 'cl-7', title: 'Configure security policies', completed: false, assignee: { id: 'u4', name: 'David Kim', avatar: 'DK' }, dueDate: '2026-02-08' }
        ],
        dueDate: '2026-02-10',
        startDate: '2026-01-25',
        tags: ['Backend', 'Security'],
        taskType: 'Development',
        dependencies: [],
        progress: 0,
        projectId: 'proj1',
        lastUpdated: '2026-01-22',
        createdDate: '2026-01-18',
        createdBy: 'u2',
        ownerId: 'u2'
      },
      {
        id: 'TASK-003',
        title: 'Update employee handbook',
        description: 'Review and update company policies and procedures',
        status: 'In Review',
        priority: 'Medium',
        assignees: [{ id: 'u3', name: 'Emily Rodriguez', avatar: 'ER', email: 'emily.r@example.com' }],
        owner: { id: 'u3', name: 'Emily Rodriguez', avatar: 'ER', email: 'emily.r@example.com', role: 'HR Manager' },
        contributors: [],
        checklist: [
          { id: 'cl-8', title: 'Review legal compliance', completed: true, assignee: { id: 'u3', name: 'Emily Rodriguez', avatar: 'ER' }, dueDate: '2026-01-15', completedBy: 'u3', completedAt: '2026-01-15T16:00:00' },
          { id: 'cl-9', title: 'Update remote work policy', completed: true, assignee: { id: 'u3', name: 'Emily Rodriguez', avatar: 'ER' }, dueDate: '2026-01-20', completedBy: 'u3', completedAt: '2026-01-20T11:30:00' },
          { id: 'cl-10', title: 'Get management approval', completed: false, dueDate: '2026-02-18' }
        ],
        dueDate: '2026-02-20',
        startDate: '2026-01-10',
        tags: ['Documentation', 'HR'],
        taskType: 'Documentation',
        dependencies: [],
        progress: 80,
        projectId: 'proj2',
        lastUpdated: '2026-01-23',
        createdDate: '2026-01-08',
        createdBy: 'u3',
        ownerId: 'u3'
      },
      {
        id: 'TASK-004',
        title: 'Setup CI/CD pipeline',
        description: 'Configure automated testing and deployment pipeline',
        status: 'Done',
        priority: 'High',
        assignees: [{ id: 'u4', name: 'David Kim', avatar: 'DK', email: 'david.k@example.com' }],
        owner: { id: 'u4', name: 'David Kim', avatar: 'DK', email: 'david.k@example.com', role: 'DevOps Engineer' },
        contributors: [
          { id: 'u2', name: 'Michael Chen', avatar: 'MC', email: 'michael.c@example.com', role: 'Senior Developer' }
        ],
        checklist: [
          { id: 'cl-11', title: 'Setup GitHub Actions', completed: true, assignee: { id: 'u4', name: 'David Kim', avatar: 'DK' }, dueDate: '2026-01-18', completedBy: 'u4', completedAt: '2026-01-18T09:00:00' },
          { id: 'cl-12', title: 'Configure test automation', completed: true, assignee: { id: 'u2', name: 'Michael Chen', avatar: 'MC' }, dueDate: '2026-01-22', completedBy: 'u2', completedAt: '2026-01-22T15:45:00' },
          { id: 'cl-13', title: 'Deploy to staging', completed: true, assignee: { id: 'u4', name: 'David Kim', avatar: 'DK' }, dueDate: '2026-01-28', completedBy: 'u4', completedAt: '2026-01-28T14:20:00' }
        ],
        dueDate: '2026-01-30',
        startDate: '2026-01-15',
        tags: ['DevOps', 'Infrastructure'],
        taskType: 'Infrastructure',
        dependencies: [],
        progress: 100,
        projectId: 'proj1',
        lastUpdated: '2026-01-20',
        createdDate: '2026-01-12',
        createdBy: 'u4',
        ownerId: 'u4'
      },
      {
        id: 'TASK-005',
        title: 'Conduct user research interviews',
        description: 'Interview 15 employees about wellness program needs',
        status: 'In Progress',
        priority: 'Medium',
        assignees: [
          { id: 'u5', name: 'Lisa Anderson', avatar: 'LA', email: 'lisa.a@example.com' },
          { id: 'u3', name: 'Emily Rodriguez', avatar: 'ER', email: 'emily.r@example.com' }
        ],
        owner: { id: 'u5', name: 'Lisa Anderson', avatar: 'LA', email: 'lisa.a@example.com', role: 'UX Researcher' },
        contributors: [
          { id: 'u3', name: 'Emily Rodriguez', avatar: 'ER', email: 'emily.r@example.com', role: 'HR Manager' },
          { id: 'u1', name: 'Sarah Johnson', avatar: 'SJ', email: 'sarah.j@example.com', role: 'Product Designer' }
        ],
        checklist: [
          { id: 'cl-14', title: 'Prepare interview questions', completed: true, assignee: { id: 'u5', name: 'Lisa Anderson', avatar: 'LA' }, dueDate: '2026-01-22', completedBy: 'u5', completedAt: '2026-01-22T10:00:00' },
          { id: 'cl-15', title: 'Schedule interviews', completed: true, assignee: { id: 'u3', name: 'Emily Rodriguez', avatar: 'ER' }, dueDate: '2026-01-25', completedBy: 'u3', completedAt: '2026-01-25T14:30:00' },
          { id: 'cl-16', title: 'Conduct interviews (8/15)', completed: false, assignee: { id: 'u5', name: 'Lisa Anderson', avatar: 'LA' }, dueDate: '2026-02-15' },
          { id: 'cl-17', title: 'Analyze findings', completed: false, assignee: { id: 'u1', name: 'Sarah Johnson', avatar: 'SJ' }, dueDate: '2026-02-22' }
        ],
        dueDate: '2026-02-25',
        startDate: '2026-01-20',
        tags: ['Research', 'UX'],
        taskType: 'Research',
        dependencies: [],
        progress: 40,
        projectId: 'proj2',
        lastUpdated: '2026-01-23',
        createdDate: '2026-01-16',
        createdBy: 'u5',
        ownerId: 'u5',
        isLive: true
      },
      {
        id: 'TASK-006',
        title: 'Fix critical security vulnerability',
        description: 'Patch SQL injection vulnerability in user input',
        status: 'To Do',
        priority: 'Critical',
        assignees: [{ id: 'u2', name: 'Michael Chen', avatar: 'MC', email: 'michael.c@example.com' }],
        owner: { id: 'u2', name: 'Michael Chen', avatar: 'MC', email: 'michael.c@example.com', role: 'Senior Developer' },
        contributors: [],
        checklist: [
          { id: 'cl-18', title: 'Identify vulnerable endpoints', completed: false, assignee: { id: 'u2', name: 'Michael Chen', avatar: 'MC' }, dueDate: '2026-01-24' },
          { id: 'cl-19', title: 'Apply security patches', completed: false, assignee: { id: 'u2', name: 'Michael Chen', avatar: 'MC' }, dueDate: '2026-01-25' }
        ],
        dueDate: '2026-01-25',
        startDate: '2026-01-23',
        tags: ['Security', 'Bug'],
        taskType: 'Bug Fix',
        dependencies: [],
        progress: 0,
        projectId: 'proj1',
        lastUpdated: '2026-01-23',
        createdDate: '2026-01-23',
        createdBy: 'u2',
        ownerId: 'u2'
      },
      {
        id: 'TASK-007',
        title: 'Design marketing campaign assets',
        description: 'Create social media graphics and email templates',
        status: 'To Do',
        priority: 'Low',
        assignees: [{ id: 'u6', name: 'Robert Taylor', avatar: 'RT', email: 'robert.t@example.com' }],
        owner: { id: 'u6', name: 'Robert Taylor', avatar: 'RT', email: 'robert.t@example.com', role: 'Marketing Designer' },
        contributors: [],
        checklist: [],
        dueDate: '2026-03-01',
        startDate: '2026-02-15',
        tags: ['Design', 'Marketing'],
        taskType: 'Design',
        dependencies: [],
        progress: 0,
        projectId: 'proj4',
        lastUpdated: '2026-01-22',
        createdDate: '2026-01-20',
        createdBy: 'u6',
        ownerId: 'u6'
      },
      {
        id: 'TASK-008',
        title: 'Database performance optimization',
        description: 'Optimize slow queries and add proper indexes',
        status: 'In Progress',
        priority: 'High',
        assignees: [{ id: 'u4', name: 'David Kim', avatar: 'DK', email: 'david.k@example.com' }],
        owner: { id: 'u4', name: 'David Kim', avatar: 'DK', email: 'david.k@example.com', role: 'DevOps Engineer' },
        contributors: [
          { id: 'u2', name: 'Michael Chen', avatar: 'MC', email: 'michael.c@example.com', role: 'Senior Developer' }
        ],
        checklist: [
          { id: 'cl-20', title: 'Analyze slow queries', completed: true, assignee: { id: 'u4', name: 'David Kim', avatar: 'DK' }, dueDate: '2026-01-25', completedBy: 'u4', completedAt: '2026-01-25T11:00:00' },
          { id: 'cl-21', title: 'Add database indexes', completed: false, assignee: { id: 'u4', name: 'David Kim', avatar: 'DK' }, dueDate: '2026-02-05' },
          { id: 'cl-22', title: 'Test performance improvements', completed: false, assignee: { id: 'u2', name: 'Michael Chen', avatar: 'MC' }, dueDate: '2026-02-10' }
        ],
        dueDate: '2026-02-12',
        startDate: '2026-01-22',
        tags: ['Backend', 'Performance'],
        taskType: 'Development',
        dependencies: ['TASK-004'],
        progress: 30,
        projectId: 'proj1',
        lastUpdated: '2026-01-23',
        createdDate: '2026-01-19',
        createdBy: 'u4',
        ownerId: 'u4',
        isLive: true
      },
      {
        id: 'TASK-009',
        title: 'Write API documentation',
        description: 'Document all REST API endpoints with examples',
        status: 'In Review',
        priority: 'Medium',
        assignees: [{ id: 'u1', name: 'Sarah Johnson', avatar: 'SJ', email: 'sarah.j@example.com' }],
        owner: { id: 'u1', name: 'Sarah Johnson', avatar: 'SJ', email: 'sarah.j@example.com', role: 'Product Designer' },
        contributors: [
          { id: 'u2', name: 'Michael Chen', avatar: 'MC', email: 'michael.c@example.com', role: 'Senior Developer' }
        ],
        checklist: [
          { id: 'cl-23', title: 'Document authentication endpoints', completed: true, assignee: { id: 'u1', name: 'Sarah Johnson', avatar: 'SJ' }, dueDate: '2026-01-28', completedBy: 'u1', completedAt: '2026-01-28T16:00:00' },
          { id: 'cl-24', title: 'Add code examples', completed: false, assignee: { id: 'u2', name: 'Michael Chen', avatar: 'MC' }, dueDate: '2026-02-10' },
          { id: 'cl-25', title: 'Review with team', completed: false, dueDate: '2026-02-15' }
        ],
        dueDate: '2026-02-18',
        startDate: '2026-01-25',
        tags: ['Documentation', 'Backend'],
        taskType: 'Documentation',
        dependencies: ['TASK-002'],
        progress: 70,
        projectId: 'proj1',
        lastUpdated: '2026-01-23',
        createdDate: '2026-01-21',
        createdBy: 'u1',
        ownerId: 'u1'
      },
      {
        id: 'TASK-010',
        title: 'Plan office layout for Dubai expansion',
        description: 'Design floor plan and furniture arrangement',
        status: 'Done',
        priority: 'Medium',
        assignees: [{ id: 'u7', name: 'Ahmed Al-Mansoori', avatar: 'AA', email: 'ahmed.m@example.com' }],
        owner: { id: 'u7', name: 'Ahmed Al-Mansoori', avatar: 'AA', email: 'ahmed.m@example.com', role: 'Facilities Manager' },
        contributors: [],
        checklist: [
          { id: 'cl-26', title: 'Measure office space', completed: true, assignee: { id: 'u7', name: 'Ahmed Al-Mansoori', avatar: 'AA' }, dueDate: '2026-01-08', completedBy: 'u7', completedAt: '2026-01-08T10:00:00' },
          { id: 'cl-27', title: 'Create floor plan', completed: true, assignee: { id: 'u7', name: 'Ahmed Al-Mansoori', avatar: 'AA' }, dueDate: '2026-01-15', completedBy: 'u7', completedAt: '2026-01-15T14:30:00' },
          { id: 'cl-28', title: 'Order furniture', completed: true, assignee: { id: 'u7', name: 'Ahmed Al-Mansoori', avatar: 'AA' }, dueDate: '2026-01-20', completedBy: 'u7', completedAt: '2026-01-20T09:15:00' }
        ],
        dueDate: '2026-01-20',
        startDate: '2026-01-05',
        tags: ['Planning', 'Facilities'],
        taskType: 'Planning',
        dependencies: [],
        progress: 100,
        projectId: 'proj3',
        lastUpdated: '2026-01-18',
        createdDate: '2026-01-02',
        createdBy: 'u7',
        ownerId: 'u7'
      },
      {
        id: 'TASK-011',
        title: 'Implement real-time notifications',
        description: 'Add WebSocket support for live notifications',
        status: 'Blocked',
        priority: 'Medium',
        assignees: [{ id: 'u2', name: 'Michael Chen', avatar: 'MC', email: 'michael.c@example.com' }],
        owner: { id: 'u2', name: 'Michael Chen', avatar: 'MC', email: 'michael.c@example.com', role: 'Senior Developer' },
        contributors: [
          { id: 'u4', name: 'David Kim', avatar: 'DK', email: 'david.k@example.com', role: 'DevOps Engineer' }
        ],
        checklist: [
          { id: 'cl-29', title: 'Setup WebSocket server', completed: false, assignee: { id: 'u4', name: 'David Kim', avatar: 'DK' }, dueDate: '2026-02-05' },
          { id: 'cl-30', title: 'Implement client connection', completed: false, assignee: { id: 'u2', name: 'Michael Chen', avatar: 'MC' }, dueDate: '2026-02-15' }
        ],
        dueDate: '2026-02-28',
        startDate: '2026-02-01',
        tags: ['Backend', 'Feature'],
        taskType: 'Development',
        dependencies: ['TASK-002', 'TASK-008'],
        progress: 10,
        projectId: 'proj1',
        lastUpdated: '2026-01-23',
        createdDate: '2026-01-22',
        createdBy: 'u2',
        ownerId: 'u2'
      },
      {
        id: 'TASK-012',
        title: 'Create wellness program survey',
        description: 'Design and distribute employee wellness survey',
        status: 'To Do',
        priority: 'Low',
        assignees: [{ id: 'u5', name: 'Lisa Anderson', avatar: 'LA', email: 'lisa.a@example.com' }],
        owner: { id: 'u5', name: 'Lisa Anderson', avatar: 'LA', email: 'lisa.a@example.com', role: 'UX Researcher' },
        contributors: [
          { id: 'u3', name: 'Emily Rodriguez', avatar: 'ER', email: 'emily.r@example.com', role: 'HR Manager' }
        ],
        checklist: [
          { id: 'cl-31', title: 'Draft survey questions', completed: false, assignee: { id: 'u5', name: 'Lisa Anderson', avatar: 'LA' }, dueDate: '2026-02-25' },
          { id: 'cl-32', title: 'Review with HR', completed: false, assignee: { id: 'u3', name: 'Emily Rodriguez', avatar: 'ER' }, dueDate: '2026-03-01' }
        ],
        dueDate: '2026-03-05',
        startDate: '2026-02-20',
        tags: ['HR', 'Research'],
        taskType: 'Research',
        dependencies: ['TASK-005'],
        progress: 0,
        projectId: 'proj2',
        lastUpdated: '2026-01-23',
        createdDate: '2026-01-21',
        createdBy: 'u5',
        ownerId: 'u5'
      },
    ]);
  }, []);

  // Permission Layer (Mocked)
  const canEditTask = useCallback((task) => {
    if (CURRENT_USER_ROLE === 'admin') return true;
    if (CURRENT_USER_ROLE === 'manager') return true;
    return task?.ownerId === 'u1'; // Current user ID
  }, []);

  const canReassignTask = useCallback((task) => {
    if (CURRENT_USER_ROLE === 'admin') return true;
    if (CURRENT_USER_ROLE === 'manager') return true;
    return false;
  }, []);

  const canBulkEdit = useCallback((tasks) => {
    if (CURRENT_USER_ROLE === 'admin') return true;
    if (CURRENT_USER_ROLE === 'manager') return true;
    return tasks?.every(task => task?.ownerId === 'u1');
  }, []);

  const canDeleteTask = useCallback((task) => {
    if (CURRENT_USER_ROLE === 'admin') return true;
    if (CURRENT_USER_ROLE === 'manager' && task?.ownerId !== 'admin') return true;
    return false;
  }, []);

  // Status Transition Configuration
  const statusTransitions = {
    'To Do': ['In Progress', 'Blocked'],
    'In Progress': ['In Review', 'Blocked', 'To Do'],
    'In Review': ['Done', 'In Progress', 'Blocked'],
    'Blocked': ['To Do', 'In Progress'],
    'Done': ['In Review']
  };

  const canTransition = useCallback((task, fromStatus, toStatus) => {
    // Find the status configuration
    const fromStatusConfig = statusConfig?.find(s => s?.id === fromStatus);
    
    // Check if transition is allowed based on configuration
    const allowedTransitions = fromStatusConfig?.allowedTransitions || [];
    if (!allowedTransitions?.includes(toStatus)) {
      return { allowed: false, reason: `Cannot transition from ${fromStatus} to ${toStatus}` };
    }

    // Check dependencies for terminal statuses
    const toStatusConfig = statusConfig?.find(s => s?.id === toStatus);
    if (toStatusConfig?.isTerminal && task?.dependencies?.length > 0) {
      const unresolvedDeps = task?.dependencies?.filter(depId => {
        const depTask = tasks?.find(t => t?.id === depId);
        return depTask && depTask?.status !== 'Done';
      });
      if (unresolvedDeps?.length > 0) {
        return { allowed: false, reason: 'Cannot complete task with unresolved dependencies' };
      }
    }

    return { allowed: true, reason: '' };
  }, [tasks, statusConfig]);

  // Analytics Signals
  const calculateTaskAging = useCallback((task) => {
    const createdDate = parseISO(task?.createdDate);
    const today = new Date();
    return differenceInDays(today, createdDate);
  }, []);

  const calculateBlockedDuration = useCallback((task) => {
    if (task?.status !== 'Blocked') return 0;
    const lastUpdated = parseISO(task?.lastUpdated);
    const today = new Date();
    return differenceInDays(today, lastUpdated);
  }, []);

  const isOverdue = useCallback((task) => {
    if (task?.status === 'Done') return false;
    const dueDate = parseISO(task?.dueDate);
    const today = new Date();
    return dueDate < today;
  }, []);

  const getRiskLevel = useCallback((task) => {
    const aging = calculateTaskAging(task);
    const blocked = calculateBlockedDuration(task);
    const overdue = isOverdue(task);

    if (overdue && task?.priority === 'Critical') return 'critical';
    if (blocked > 7) return 'high';
    if (overdue || aging > 30) return 'medium';
    return 'low';
  }, [calculateTaskAging, calculateBlockedDuration, isOverdue]);

  // Check if task is blocked
  const isTaskBlocked = useCallback((task) => {
    if (task?.status === 'Blocked') return true;
    
    // Check if task has unresolved dependencies
    if (task?.dependencies?.length > 0) {
      const unresolvedDeps = task?.dependencies?.filter(depId => {
        const depTask = tasks?.find(t => t?.id === depId);
        return depTask && depTask?.status !== 'Done';
      });
      return unresolvedDeps?.length > 0;
    }
    
    return false;
  }, [tasks]);

  // Get block reason
  const getBlockReason = useCallback((task) => {
    if (task?.status === 'Blocked') {
      return 'Task marked as blocked';
    }
    
    if (task?.dependencies?.length > 0) {
      const unresolvedDeps = task?.dependencies?.filter(depId => {
        const depTask = tasks?.find(t => t?.id === depId);
        return depTask && depTask?.status !== 'Done';
      });
      
      if (unresolvedDeps?.length > 0) {
        return `Waiting on ${unresolvedDeps?.length} unresolved ${unresolvedDeps?.length === 1 ? 'dependency' : 'dependencies'}`;
      }
    }
    
    return null;
  }, [tasks]);

  // Task Operations
  const updateTask = useCallback((taskId, changes) => {
    setTasks(prev => prev?.map(task => 
      task?.id === taskId 
        ? { ...task, ...changes, lastUpdated: new Date()?.toISOString()?.split('T')?.[0] }
        : task
    ));
  }, []);

  const bulkUpdateTasks = useCallback((taskIds, action, payload) => {
    const tasksToUpdate = tasks?.filter(t => taskIds?.includes(t?.id));
    
    // Permission check
    if (!canBulkEdit(tasksToUpdate)) {
      return { success: false, message: 'You do not have permission to edit these tasks' };
    }

    // Save to undo stack with action metadata
    setUndoStack(prev => [...prev, { 
      taskIds, 
      previousState: tasksToUpdate,
      action: { type: action, value: payload }
    }]);

    setTasks(prev => prev?.map(task => {
      if (!taskIds?.includes(task?.id)) return task;

      let updates = { lastUpdated: new Date()?.toISOString()?.split('T')?.[0] };

      switch (action) {
        case 'status':
          updates.status = payload;
          break;
        case 'priority':
          updates.priority = payload;
          break;
        case 'changeOwner':
          // Replace task owner (single accountability)
          updates.owner = payload;
          updates.ownerId = payload?.id;
          break;
        case 'addContributors':
          // Add contributors without removing existing ones
          const existingContributorIds = task?.contributors?.map(c => c?.id) || [];
          const newContributors = payload?.filter(userId => !existingContributorIds?.includes(userId))?.map(userId => {
              // Find user details from payload or mock data
              const userOptions = [
                { id: 'u1', name: 'Sarah Johnson', avatar: 'SJ', email: 'sarah.j@example.com', role: 'Product Designer' },
                { id: 'u2', name: 'Michael Chen', avatar: 'MC', email: 'michael.c@example.com', role: 'Senior Developer' },
                { id: 'u3', name: 'Emily Rodriguez', avatar: 'ER', email: 'emily.r@example.com', role: 'HR Manager' },
                { id: 'u4', name: 'David Kim', avatar: 'DK', email: 'david.k@example.com', role: 'DevOps Engineer' },
                { id: 'u5', name: 'Lisa Anderson', avatar: 'LA', email: 'lisa.a@example.com', role: 'UX Researcher' },
                { id: 'u6', name: 'Robert Taylor', avatar: 'RT', email: 'robert.t@example.com', role: 'Marketing Designer' },
                { id: 'u7', name: 'Ahmed Al-Mansoori', avatar: 'AA', email: 'ahmed.m@example.com', role: 'Facilities Manager' },
              ];
              return userOptions?.find(u => u?.id === userId);
            })?.filter(Boolean);
          updates.contributors = [...(task?.contributors || []), ...newContributors];
          break;
        case 'assignee':
          // Legacy support - avoid duplicates
          const existingIds = task?.assignees?.map(a => a?.id) || [];
          if (!existingIds?.includes(payload?.id)) {
            updates.assignees = [...(task?.assignees || []), payload];
          }
          break;
        case 'addTag':
          if (!task?.tags?.includes(payload)) {
            updates.tags = [...(task?.tags || []), payload];
          }
          break;
        case 'removeTag':
          updates.tags = task?.tags?.filter(t => t !== payload);
          break;
        case 'dueDate':
          updates.dueDate = payload;
          break;
        case 'delete':
          // Mark for deletion (will be filtered out)
          return null;
        default:
          break;
      }

      return { ...task, ...updates };
    })?.filter(Boolean)); // Remove null entries (deleted tasks)

    return { success: true, message: `${taskIds?.length} tasks updated` };
  }, [tasks, canBulkEdit]);

  const transitionTaskStatus = useCallback((taskId, fromStatus, toStatus) => {
    const task = tasks?.find(t => t?.id === taskId);
    if (!task) return { success: false, message: 'Task not found' };

    const transition = canTransition(task, fromStatus, toStatus);
    if (!transition?.allowed) {
      return { success: false, message: transition?.reason };
    }

    updateTask(taskId, { status: toStatus });
    return { success: true, message: 'Status updated' };
  }, [tasks, canTransition, updateTask]);

  const undoLastBulkAction = useCallback(() => {
    if (undoStack?.length === 0) return;

    const lastAction = undoStack?.[undoStack?.length - 1];
    setTasks(prev => prev?.map(task => {
      const previousTask = lastAction?.previousState?.find(t => t?.id === task?.id);
      return previousTask || task;
    }));

    setUndoStack(prev => prev?.slice(0, -1));
  }, [undoStack]);

  // Saved Views Operations
  const saveView = useCallback((viewData) => {
    const newView = {
      id: `view-${Date.now()}`,
      name: viewData?.name,
      isDefault: viewData?.isDefault || false,
      isShared: viewData?.isShared || false,
      filters: { ...filters },
      sortBy,
      viewType: currentView,
      visibleColumns: viewData?.visibleColumns || []
    };
    setSavedViews(prev => [...prev, newView]);
    return newView;
  }, [filters, sortBy, currentView]);

  const loadView = useCallback((viewId) => {
    const view = savedViews?.find(v => v?.id === viewId);
    if (!view) return;

    setFilters(view?.filters);
    setSortBy(view?.sortBy);
    setCurrentView(view?.viewType);
  }, [savedViews]);

  const setDefaultView = useCallback((viewId) => {
    setSavedViews(prev => prev?.map(view => ({
      ...view,
      isDefault: view?.id === viewId
    })));
  }, []);

  const deleteView = useCallback((viewId) => {
    setSavedViews(prev => prev?.filter(v => v?.id !== viewId));
  }, []);

  // Filtering and Sorting
  const filteredTasks = useMemo(() => {
    let result = [...tasks];

    // Search filter
    if (searchTerm) {
      const search = searchTerm?.toLowerCase();
      result = result?.filter(task => 
        task?.title?.toLowerCase()?.includes(search) ||
        task?.id?.toLowerCase()?.includes(search) ||
        task?.description?.toLowerCase()?.includes(search)
      );
    }

    // Project filter
    if (selectedProject !== 'all') {
      result = result?.filter(task => task?.projectId === selectedProject);
    }

    // Status filter
    if (filters?.status?.length > 0) {
      result = result?.filter(task => filters?.status?.includes(task?.status));
    }

    // Assignee filter
    if (filters?.ownerIds?.length > 0) {
      result = result?.filter(task => 
        task?.assignees?.some(a => filters?.ownerIds?.includes(a?.id))
      );
    }

    // Priority filter
    if (filters?.priorities?.length > 0) {
      result = result?.filter(task => filters?.priorities?.includes(task?.priority));
    }

    // Tags filter
    if (filters?.tags?.length > 0) {
      result = result?.filter(task => 
        task?.tags?.some(tag => filters?.tags?.includes(tag))
      );
    }

    // Date range filter
    if (filters?.dateRange?.start) {
      result = result?.filter(task => task?.dueDate >= filters?.dateRange?.start);
    }
    if (filters?.dateRange?.end) {
      result = result?.filter(task => task?.dueDate <= filters?.dateRange?.end);
    }

    // Task type filter
    if (filters?.taskType) {
      result = result?.filter(task => task?.taskType === filters?.taskType);
    }

    // Dependencies filter
    if (filters?.hasDependencies === 'yes') {
      result = result?.filter(task => task?.dependencies?.length > 0);
    } else if (filters?.hasDependencies === 'no') {
      result = result?.filter(task => task?.dependencies?.length === 0);
    }

    // Live tasks filter
    if (filters?.liveTasks) {
      result = result?.filter(task => task?.isLive === true);
    }

    // Sorting
    result?.sort((a, b) => {
      switch (sortBy) {
        case 'priority': {
          const priorityOrder = { 'Critical': 0, 'High': 1, 'Medium': 2, 'Low': 3 };
          return priorityOrder?.[a?.priority] - priorityOrder?.[b?.priority];
        }
        case 'dueDate':
          return new Date(a?.dueDate) - new Date(b?.dueDate);
        case 'createdDate':
          return new Date(b?.createdDate) - new Date(a?.createdDate);
        case 'lastUpdated':
          return new Date(b?.lastUpdated) - new Date(a?.lastUpdated);
        default:
          return 0;
      }
    });

    return result;
  }, [tasks, searchTerm, selectedProject, filters, sortBy]);

  // Selection Operations
  const toggleTaskSelection = useCallback((taskId) => {
    setSelectedTasks(prev => 
      prev?.includes(taskId)
        ? prev?.filter(id => id !== taskId)
        : [...prev, taskId]
    );
  }, []);

  const selectAllTasks = useCallback(() => {
    setSelectedTasks(filteredTasks?.map(t => t?.id));
  }, [filteredTasks]);

  const clearSelection = useCallback(() => {
    setSelectedTasks([]);
  }, []);

  return {
    // State
    tasks: filteredTasks,
    allTasks: tasks,
    currentView,
    searchTerm,
    selectedProject,
    sortBy,
    sortDirection,
    selectedTasks,
    isFilterPanelOpen,
    filters,
    savedViews,
    density,
    undoStack,
    statusConfig,

    // Setters
    setCurrentView,
    setSearchTerm,
    setSelectedProject,
    setSortBy,
    setSortDirection,
    setIsFilterPanelOpen,
    setFilters,
    setDensity,
    setStatusConfig,

    // Task Operations
    updateTask,
    bulkUpdateTasks,
    transitionTaskStatus,
    undoLastBulkAction,

    // Selection Operations
    toggleTaskSelection,
    selectAllTasks,
    clearSelection,

    // Saved Views Operations
    saveView,
    loadView,
    setDefaultView,
    deleteView,

    // Permission Checks
    canEditTask,
    canReassignTask,
    canBulkEdit,
    canDeleteTask,
    canTransition,

    // Analytics
    calculateTaskAging,
    calculateBlockedDuration,
    isOverdue,
    getRiskLevel,
    isTaskBlocked,
    getBlockReason,
  };
};

export default useTaskBoardController;